import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  finalize,
  retry,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { AuthService } from '../auth.service';
import { TokenManager } from '../tokens/token-manager';

@Injectable()
export class JWTInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshToken$ = new BehaviorSubject<boolean | null>(null);

  private excludedEndpoints: string[] = [
    'auth',
    'assets',
    'usuario/datos-ciudadano/validar',
    'usuario/registrar',
    'parametro/sistema',
    'validacion-facial/realizar',
    'catalogo/provincia',
    'catalogo/municipio',
    'catalogo/sector',
    'catalogo/bandera',
    'web-socket/generar',
    'foto-ciudadano/consultar',
    '/recaptcha/validar',
    '/usuario/medio-contacto/inicializar',
    '/usuario/medio-contacto/verificar',
  ];

  constructor(private readonly authService: AuthService) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const requiresAuthentication = !this.excludedEndpoints.some(x =>
      req.url.includes(x)
    );

    if (!req.url.includes(environment.BASE_URL)) return next.handle(req);

    if (!requiresAuthentication && !req.headers.has('Authorization')) {
      req = this.addApiKeyHeader(req);

      return next.handle(req);
    }

    const token = TokenManager.get(TokenManager.TOKEN_ID);

    if (!token) {
      return this.authService.logout();
    }

    if (TokenManager.isExpired(TokenManager.TOKEN_ID)) {
      return this.handleExpiredToken(req, next);
    }

    return this.handleRequestWithToken(req, next);
  }

  private addAuthorizationHeader(req: HttpRequest<unknown>) {
    const token = TokenManager.get(TokenManager.TOKEN_ID);
    const headers = req.headers.set(
      TokenManager.HEADER_TYPE,
      `${TokenManager.HEADER_PREFIX} ${token}`
    );
    return req.clone({ headers });
  }

  private addApiKeyHeader(req: HttpRequest<unknown>) {
    const headers = req.headers.set('x-api-key', environment.API_KEY as string);
    return req.clone({ headers });
  }

  private handleExpiredToken(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      return this.refreshToken$.pipe(
        filter(result => result !== null),
        take(1),
        switchMap(() => this.handleRequestWithToken(req, next))
      );
    }

    this.isRefreshing = true;
    this.refreshToken$.next(null);

    return this.authService.refreshToken().pipe(
      retry(2),
      tap(() => this.refreshToken$.next(true)),
      catchError(() => this.authService.logout()),
      switchMap(() => this.handleRequestWithToken(req, next)),
      finalize(() => (this.isRefreshing = false))
    );
  }

  private handleRequestWithToken(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    req = this.addAuthorizationHeader(req);

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if ([HttpStatusCode.Unauthorized].includes(error.status)) {
          return this.authService.logout();
        }

        return throwError(() => error);
      })
    );
  }
}
